import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import OAuthButton from './OAuthButton';

function Signup() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState('');
    
    // Form state

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Display name, capped to match the server (D24/D39). Email is the
    // account identifier and is required (D40).
    const USERNAME_MAX = 10;

    // Form validation
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isPasswordValid = password.length >= 8;
    const doPasswordsMatch = password === passwordConfirm;
    const isUsernameValid = username.trim().length > 0 && username.trim().length <= USERNAME_MAX;
    const isFormValid = isUsernameValid && email.length > 0 && isEmailValid && isPasswordValid && doPasswordsMatch;

    const submitForm = async (e) => {
        if (e) {
            e.preventDefault();
        }
        
        if (password.length < 8) {
            setErrorMessage('Password must be at least 8 characters long.');
            return;
        }
        
        if (!isEmailValid) {
            setErrorMessage('Please enter a valid email address.');
            return;
        }

        if (!isUsernameValid) {
            setErrorMessage(`Username must be between 1 and ${USERNAME_MAX} characters.`);
            return;
        }
        
        if (!doPasswordsMatch) {
            setErrorMessage('Passwords do not match.');
            return;
        }
        
        setErrorMessage(''); // Clear any previous errors
        setIsSubmitting(true);
        
        try {
            await register(username, password, email);
            navigate('/');
        } catch (error) {
            console.log('Registration error:', error);
            setErrorMessage('Registration failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && isFormValid && !isSubmitting) {
            submitForm(e);
        }
    }
    
    return (
        <div
            onClick={() => navigate("/")}
            className="flex-grow w-full flex items-center justify-center p-3 relative z-10"
        >
        <div className="max-w-md w-full">
          {/* Decorative close affordance - hidden for now. The backdrop behind
              it handles the dismiss, so this is deliberately not interactive. */}
          {/*
          <div className="flex justify-end mb-2">
            <div
              aria-hidden="true"
              className="w-7 h-7 flex items-center justify-center bg-nlg-gold text-black rounded-full pointer-events-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
          </div>
          */}
            <section
                id="signup"
                onClick={(e) => e.stopPropagation()}
                className="w-full bg-nlg-block border border-nlg-rule rounded-2xl p-5 sm:p-7"
            >
                <div className="text-center mb-6">
                    <h3 className="font-display font-black text-nlg-cream text-xl sm:text-2xl mb-1">Join the Fun</h3>
                    <p className="text-nlg-muted text-sm">Create your account and start gaming</p>
                </div>

                {/* Same button as /login, and the same endpoint - the Google
                    flow doesn't distinguish signing in from signing up.
                    `auth-google-callback` looks for an existing account by
                    googleId then by email; finding neither, it sends the user
                    on to pick a display name and `auth-complete-signup`
                    creates the record. Offering it here just means a new user
                    no longer has to guess that "sign in" is also how you join. */}
                <div className="oauth-buttons space-y-3">
                    <OAuthButton provider="google" label="Sign up with Google" icon="/svgs/google.svg" />
                </div>

                <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-nlg-rule"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-nlg-block text-nlg-muted font-medium">OR</span>
                    </div>
                </div>

                {/* Consolidated form (previously UserForm component) */}
                <div className="space-y-4">
                    <div>
                        <label htmlFor="signup-username" className="block text-nlg-text font-medium mb-1">Username</label>
                        <input
                            id="signup-username"
                            type="text"
                            maxLength={USERNAME_MAX}
                            placeholder="How friends will recognize you"
                            value={username}
                            onChange={(e) => { setUsername(e.target.value) }}
                            onKeyPress={handleKeyPress}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-nlg-rule text-nlg-text placeholder:text-nlg-muted focus:outline-none focus:border-nlg-gold/50"
                        />
                    </div>

                    <div>
                        <label htmlFor="signup-email" className="block text-nlg-text font-medium mb-1">Email</label>
                        <input
                            id="signup-email"
                            type="email"
                            placeholder="your.email@example.com"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value) }}
                            onKeyPress={handleKeyPress}
                            className={`w-full px-3 py-2 rounded-lg bg-white/5 border text-nlg-text placeholder:text-nlg-muted focus:outline-none transition-colors duration-150 ${
                                email.length > 0 && !isEmailValid ? 'border-nlg-coral/50' : 'border-nlg-rule focus:border-nlg-gold/50'
                            }`}
                        />
                        {email.length > 0 && !isEmailValid && (
                            <p className="text-nlg-coral text-sm mt-1">Please enter a valid email address</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="signup-password" className="block text-nlg-text font-medium mb-1">Password</label>
                        <input
                            type="password"
                            id="signup-password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value) }}
                            onKeyPress={handleKeyPress}
                            className={`w-full px-3 py-2 rounded-lg bg-white/5 border text-nlg-text placeholder:text-nlg-muted focus:outline-none transition-colors duration-150 ${
                                password.length > 0 && !isPasswordValid ? 'border-nlg-coral/50' : 'border-nlg-rule focus:border-nlg-gold/50'
                            }`}
                        />
                        {password.length > 0 && !isPasswordValid && (
                            <p className="text-nlg-coral text-sm mt-1">Password must be at least 8 characters long</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="signup-password-confirm" className="block text-nlg-text font-medium mb-1">Confirm Password</label>
                        <input
                            type="password"
                            id="signup-password-confirm"
                            placeholder="••••••••"
                            value={passwordConfirm}
                            onChange={(e) => { setPasswordConfirm(e.target.value) }}
                            onKeyPress={handleKeyPress}
                            className={`w-full px-3 py-2 rounded-lg bg-white/5 border text-nlg-text placeholder:text-nlg-muted focus:outline-none transition-colors duration-150 ${
                                passwordConfirm.length > 0 && !doPasswordsMatch ? 'border-nlg-coral/50' : 'border-nlg-rule focus:border-nlg-gold/50'
                            }`}
                        />
                        {passwordConfirm.length > 0 && !doPasswordsMatch && (
                            <p className="text-nlg-coral text-sm mt-1">Passwords do not match</p>
                        )}
                    </div>

                    {errorMessage && (
                        <div className="text-nlg-coral text-sm bg-nlg-coral/10 border border-nlg-coral/30 rounded-lg p-3" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={submitForm}
                        disabled={!isFormValid || isSubmitting}
                        className={`w-full py-2 px-4 rounded-full font-bold mt-4 transition-transform duration-150 ${
                            isFormValid && !isSubmitting
                                ? 'bg-nlg-gold text-nlg-bg hover:scale-[1.02]'
                                : 'bg-white/5 border border-nlg-rule text-nlg-muted cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting ? 'Creating Account...' : 'Create Account'}
                    </button>
                </div>

                <div className="text-center pt-3 border-t border-nlg-rule mt-4">
                    <p className="text-sm text-nlg-muted">
                        Already have an account?{' '}
                        <Link to="/login" className="text-nlg-gold font-semibold transition-colors duration-150">
                            Sign In
                        </Link>
                    </p>
                </div>
            </section>
        </div>
        </div>
    );
}

export default Signup;
